/**
 * PORTED — RO-3-00g / #44474.
 * Origin: `excellent/app/_chrome/create-menu.tsx`.
 *
 * Changes:
 *   next/navigation → @excellent/ui/router
 *   `createProject` → `apiCall(projectActions.create)` (RO-2-09b)
 *   `quickCreateTask` → `apiCall(taskActions.quickCreate)` (slice for this item;
 *     full task-actions family remains RO-2-09c / #44416)
 */

import { useEffect, useState, useTransition } from "react";
import { useNavigate, usePathname, useSearch } from "@excellent/ui/router";
import {
  Button,
  CreateComposer,
  ComposerSelectPill,
  IconTooltip,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@excellent/ui";
import {
  CircleDashed,
  Flag,
  FolderKanban,
  FolderPlus,
  LineChart,
  Plus,
  Target,
} from "@excellent/ui/icons";
import { apiCall, useApi, attachFilesToEntity } from "@excellent/ui/api";
import { projectsTree } from "@excellent/ui/api/contracts/projects-tree";
import { projectActions } from "@excellent/ui/api/contracts/project-actions";
import { taskActions } from "@excellent/ui/api/contracts/task-actions";

type CreateType = "issue" | "project" | "goal" | "product";

/** Shared open/type state for the one create modal — used by both the visible
 *  `<CreateMenu>` button and the headless global `<CreateMenuListener>`. */
function useQuickCreateState() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<CreateType>("issue");
  // A preset status the composer opens on (the Issues status-section "+"). Carried
  // so `excellent:create-task { detail:{status} }` lands the new issue in that bucket.
  const [presetStatus, setPresetStatus] = useState<string | undefined>(undefined);
  const openAt = (t: CreateType, status?: string) => {
    setType(t);
    setPresetStatus(status);
    setOpen(true);
  };
  return { open, setOpen, type, setType, presetStatus, setPresetStatus, openAt };
}

/**
 * The single create affordance in the chrome — a blue "+" (the cobalt `default`
 * primary, the same 28px size as its neighbors so the row is even; the color, not
 * the size, marks it as THE action) that opens ONE Linear-style modal you can start
 * typing in immediately (Matt, 2026-07-16). Gone is the old
 * "+ New ▾" dropdown: instead of picking a type first, the modal opens on the Issue
 * composer with the title focused, and a type switcher at the top swaps to Project
 * (inline) or hands off to the fuller Goal / Product forms.
 *
 * This is the VISIBLE button (Issues surfaces mount it, project-scoped via
 * `defaultProjectSlug`). The dock/desktop `excellent:create-*` command bridge lives
 * separately in `<CreateMenuListener>` so it's mounted once globally — clicking this
 * button opens the modal directly; it does NOT listen for the palette events (that
 * would double-open where both are mounted).
 */
export function CreateMenu({
  defaultProjectSlug,
  defaultSectionSlug,
}: {
  defaultProjectSlug?: string;
  defaultSectionSlug?: string;
}) {
  const { open, setOpen, type, setType, presetStatus, openAt } = useQuickCreateState();

  return (
    <>
      <IconTooltip label="Create">
        <Button
          variant="default"
          size="icon-tb"
          aria-label="Create"
          onClick={() => openAt("issue")}
        >
          <Plus strokeWidth={2.25} />
        </Button>
      </IconTooltip>

      <QuickCreateDialog
        open={open}
        onOpenChange={setOpen}
        type={type}
        onTypeChange={setType}
        defaultProjectSlug={defaultProjectSlug}
        defaultSectionSlug={defaultSectionSlug}
        defaultStatus={presetStatus}
      />
    </>
  );
}

/**
 * The headless global create bridge — mounted ONCE in the chrome (beside `DockBar`
 * in `keyboard-nav-provider`) so the dock searchbar's "Create new …" commands and
 * the desktop app menu (both dispatch `excellent:create-*`) have a listener on EVERY
 * page, not just the Issues surfaces where the visible `<CreateMenu>` button happens
 * to live. Renders no button — just the shared modal + the event bridge. Issue/
 * Project open the modal inline; Goal/Product route to their fuller forms.
 *
 * Being the SOLE listener (the button no longer listens) means exactly one modal
 * opens per command, even on pages that also mount the `<CreateMenu>` button. It is
 * unscoped by design: a global command carries no project context, so the modal
 * opens with the project picker for the user to choose.
 */
export function CreateMenuListener() {
  const router = useNavigate();
  const { open, setOpen, type, setType, presetStatus, setPresetStatus } =
    useQuickCreateState();

  // Bridge the dock/desktop create commands (which only dispatch events) into the
  // one modal — so every create entry point converges here. Goal/Property have no
  // inline composer, so those route straight to their fuller forms. A task command
  // may carry a `{ status }` (the Issues status-section "+"), which opens the
  // composer preset to that bucket.
  useEffect(() => {
    const openTask = (e: Event) => {
      const status = (e as CustomEvent<{ status?: string }>).detail?.status;
      setType("issue");
      setPresetStatus(status);
      setOpen(true);
    };
    const openProject = () => {
      setType("project");
      setPresetStatus(undefined);
      setOpen(true);
    };
    const openGoal = () => router.push("/workflows/new");
    const openProperty = () => router.push("/properties/new");
    window.addEventListener("excellent:create-task", openTask);
    window.addEventListener("excellent:create-goal", openGoal);
    window.addEventListener("excellent:create-project", openProject);
    window.addEventListener("excellent:create-property", openProperty);
    return () => {
      window.removeEventListener("excellent:create-task", openTask);
      window.removeEventListener("excellent:create-goal", openGoal);
      window.removeEventListener("excellent:create-project", openProject);
      window.removeEventListener("excellent:create-property", openProperty);
    };
  }, [router, setOpen, setType, setPresetStatus]);

  return (
    <QuickCreateDialog
      open={open}
      onOpenChange={setOpen}
      type={type}
      onTypeChange={setType}
      defaultStatus={presetStatus}
    />
  );
}

const CREATE_TYPES: {
  value: CreateType;
  label: string;
  icon: typeof Plus;
  /** Route target for types whose full setup doesn't fit the quick modal. */
  fullFormHref?: string;
}[] = [
  { value: "issue", label: "Issue", icon: FolderKanban },
  { value: "project", label: "Project", icon: FolderPlus },
  { value: "goal", label: "Goal", icon: Target, fullFormHref: "/workflows/new" },
  {
    value: "product",
    label: "Product",
    icon: LineChart,
    fullFormHref: "/properties/new",
  },
];

const PRIORITY_OPTIONS = [
  { value: "urgent", label: "Urgent" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];
const STATUS_OPTIONS = [
  { value: "backlog", label: "Backlog" },
  { value: "todo", label: "Todo" },
  { value: "in_progress", label: "In progress" },
  { value: "review", label: "In review" },
  { value: "done", label: "Done" },
];

/**
 * The one create modal — a single Linear-style `CreateComposer` for every type.
 * A breadcrumb type switcher swaps entity; Issue + Project create inline (with
 * property pills + attachments), Goal + Product carry the typed title into their
 * fuller forms via the Expand affordance. DRY: one composer, no per-type chrome.
 */
function QuickCreateDialog({
  open,
  onOpenChange,
  type,
  onTypeChange,
  defaultProjectSlug,
  defaultSectionSlug,
  defaultStatus,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: CreateType;
  onTypeChange: (t: CreateType) => void;
  defaultProjectSlug?: string;
  defaultSectionSlug?: string;
  /** Preset status the Issue composer opens on (the Issues status-section "+"). */
  defaultStatus?: string;
}) {
  const router = useNavigate();
  const pathname = usePathname();
  const searchParams = useSearch();
  const { data } = useApi(projectsTree.get, {});
  const projects = (data?.projects ?? []).filter((p) => !p.pending);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [createMore, setCreateMore] = useState(false);
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  // Issue pills.
  const [projectSlug, setProjectSlug] = useState("");
  const [priority, setPriority] = useState("");
  const [status, setStatus] = useState("");

  // Open the Issue composer on the caller's preset status — the Issues
  // status-section "+" lands the new issue in that bucket (Backlog/Todo/…).
  useEffect(() => {
    if (open) setStatus(defaultStatus ?? "");
  }, [open, defaultStatus]);

  const active = CREATE_TYPES.find((t) => t.value === type) ?? CREATE_TYPES[0]!;
  const selectedProject =
    projectSlug || defaultProjectSlug || projects[0]?.slug || "";

  function reset() {
    setTitle("");
    setDescription("");
    setFiles([]);
    setError(null);
    setProjectSlug("");
    setPriority("");
    setStatus("");
  }
  function handleOpenChange(next: boolean) {
    onOpenChange(next);
    if (!next) reset();
  }

  const canSubmit =
    title.trim().length > 0 && (type !== "issue" || Boolean(selectedProject));

  function submit() {
    setError(null);
    startTransition(async () => {
      // Goal / Product: carry the title into the fuller form (the Expand target).
      if (active.fullFormHref) {
        onOpenChange(false);
        const t = title.trim();
        router.push(
          t
            ? `${active.fullFormHref}?title=${encodeURIComponent(t)}`
            : active.fullFormHref,
        );
        reset();
        return;
      }
      if (type === "project") {
        const res = await apiCall(projectActions.create, {
          body: {
            name: title.trim(),
            description: description.trim() || undefined,
          },
        });
        onOpenChange(false);
        reset();
        router.push(res.redirectTo);
        return;
      }
      // Issue.
      const res = await apiCall(taskActions.quickCreate, {
        body: {
          title,
          projectSlug: selectedProject,
          sectionSlug:
            selectedProject === defaultProjectSlug ? defaultSectionSlug : undefined,
          description: description.trim() || undefined,
          priority: priority || undefined,
          status: status || undefined,
        },
      });
      if (!res.ok) {
        setError(
          res.reason === "title-required"
            ? "Give the issue a title."
            : "Couldn't create the issue — pick a project and try again.",
        );
        return;
      }
      if (files.length) {
        try {
          await attachFilesToEntity("task", res.id, files);
        } catch {
          // The issue was created; a failed attachment shouldn't block the flow.
        }
      }
      if (createMore) {
        reset();
        router.refresh();
        return;
      }
      onOpenChange(false);
      reset();
      // Open the new issue in place (preserve filters), then refresh the list.
      const next = new URLSearchParams(searchParams.toString());
      next.set("task", String(res.id));
      router.push(`${pathname}?${next.toString()}`);
      router.refresh();
    });
  }

  const typeSwitcher = (
    <Select value={type} onValueChange={(v) => onTypeChange(v as CreateType)}>
      <SelectTrigger variant="pill" size="sm" aria-label="Type" className="gap-1.5">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {CREATE_TYPES.map((t) => {
          const Icon = t.icon;
          return (
            <SelectItem key={t.value} value={t.value}>
              <Icon />
              {t.label}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );

  return (
    <CreateComposer
      open={open}
      onOpenChange={handleOpenChange}
      context={typeSwitcher}
      heading={`New ${active.label.toLowerCase()}`}
      expandHref={active.fullFormHref}
      onExpand={() => onOpenChange(false)}
      title={title}
      onTitleChange={setTitle}
      titlePlaceholder={`${active.label} title`}
      description={description}
      onDescriptionChange={active.fullFormHref ? undefined : setDescription}
      attachments={files}
      onAttachmentsChange={setFiles}
      createMore={type === "issue" ? createMore : undefined}
      onCreateMoreChange={type === "issue" ? setCreateMore : undefined}
      submitLabel={
        type === "issue"
          ? "Create issue"
          : type === "project"
            ? "Create project"
            : `Continue to ${active.label.toLowerCase()} setup`
      }
      onSubmit={submit}
      canSubmit={canSubmit}
      pending={pending}
      error={error}
      pills={
        type === "issue" ? (
          <>
            <ComposerSelectPill
              icon={<CircleDashed />}
              placeholder="Status"
              value={status}
              onValueChange={setStatus}
              options={STATUS_OPTIONS}
              ariaLabel="Status"
            />
            <ComposerSelectPill
              icon={<Flag />}
              placeholder="Priority"
              value={priority}
              onValueChange={setPriority}
              options={PRIORITY_OPTIONS}
              ariaLabel="Priority"
            />
            <ComposerSelectPill
              icon={<FolderKanban />}
              placeholder="Project"
              value={selectedProject}
              onValueChange={setProjectSlug}
              options={projects.map((p) => ({ value: p.slug, label: p.name }))}
              ariaLabel="Project"
            />
          </>
        ) : undefined
      }
    />
  );
}
